import React from "react";
import APIHelper from "@giscon/utils.api-helper-esri";

class PrintLogik extends React.Component {
  constructor(props) {
    super(props);
    this.onPrintError = props.onPrintError;
    this.onPrintReady = props.onPrintReady;
    this.config = props.config;
  }

  getPointGeoJSON = (geometry, attributes, id) => {
    const res = {
      type: "Feature",
      id: `point_${Date.now()}`,
      geometry: {
        type: "Point",
        coordinates: [geometry.x, geometry.y, 0],
      },
      properties: attributes || {},
    };
    res.properties.type = "point";
    res.properties.description = "";
    res.properties._gx_style = id;
    return res;
  };

  getPolygonGeoJSON = (geometry, attributes, id) => {
    const res = {
      type: "Feature",
      id: 'Gebietsgrenze',
      geometry: {
        type: "Polygon",
        coordinates: geometry.rings,
      },
      properties: attributes || {},
    };
    res.properties.type = "linepolygon";
    res.properties.description = "";
    res.properties._gx_style = id;
    return res;
  };

  getPolylineGeoJSON = (geometry, attributes, id) => {
    const res = {
      type: "Feature",
      id: `linepolygon_${Date.now()}`,
      geometry: {
        type: "MultiLineString",
        coordinates: geometry.paths,
      },
      properties: attributes || {},
    };
    res.properties.type = "linepolygon";
    res.properties.description = "";
    res.properties._gx_style = id;
    return res;
  };

  getPointStyleGeoJSON = (symbol, id) => {
    let res;
    if (symbol.type === "simplemarkersymbol") {
      res = {
        id,
        zIndex: 10,
        externalGraphic: "",
        rotation: symbol.angle,
        color: symbol.color.toHex(),
        opacity: symbol.color.a,
        graphicHeight: symbol.size,
        graphicWidth: symbol.size,
        graphicXOffset: symbol.xoffset,
        graphicYOffset: symbol.yoffset,
      };
    } else if (symbol.type === "picturemarkersymbol") {
      res = {
        id,
        zIndex: 10,
        rotation: symbol.angle,
        externalGraphic: symbol.url,
        graphicHeight: symbol.height,
        graphicWidth: symbol.width,
        graphicXOffset: symbol.xoffset,
        graphicYOffset: symbol.yoffset,
      };
    } else if (symbol.type === "textsymbol") {
      res = {
        id,
        zIndex: 10,
        externalGraphic: "",
        rotation: symbol.angle,
        fontColor: symbol.color.toHex(),
        fontSize: symbol.font.size,
        fontWeight: symbol.font.weight,
        graphicHeight: symbol.height,
        graphicWidth: symbol.width,
        labelXOffset: symbol.xoffset,
        labelYOffset: symbol.yoffset,
        label: symbol.text,
      };
    }
    return res;
  };

  getPolygonStyleGeoJSON = (symbol, id) => {
    const res = {
      id,
      zIndex: 10,
      fillColor: symbol.color.toHex(),
      fillOpacity: symbol.style !== "solid" ? 0 : symbol.color.a,
      strokeWidth: symbol.outline.width,
      strokeColor: symbol.outline.color.toHex(),
      strokeOpacity:
        symbol.outline.style !== "none" ? symbol.outline.color.a : 0,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    };
    return res;
  };

  getPolylineStyleGeoJSON = (symbol, id) => {
    const res = {
      id,
      zIndex: 10,
      fillColor: symbol.color.toHex(),
      fillOpacity: symbol.color.a,
      strokeWidth: symbol.width,
      strokeColor: symbol.color.toHex(),
      strokeOpacity: symbol.color.a,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    };
    return res;
  };

  getGraphicAsGeoJSON = (graphic, id) => {
    const res = {};
    const geometry = APIHelper.getGeometry(graphic);
    const attributes = APIHelper.getAttributes(graphic);
    switch (APIHelper.getGeometryType(geometry)) {
      case "polygon":
        res.geometry = this.getPolygonGeoJSON(geometry, attributes, id);
        res.symbol = this.getPolygonStyleGeoJSON(graphic.symbol, id);
        break;
      case "polyline":
        res.geometry = this.getPolylineGeoJSON(geometry, attributes, id);
        res.symbol = this.getPolylineStyleGeoJSON(graphic.symbol, id);
        break;
      case "point":
        res.geometry = this.getPointGeoJSON(geometry, attributes, id);
        res.symbol = this.getPointStyleGeoJSON(graphic.symbol, id);
        break;
      default:
        console.error(
          "Geometry type not supported by the print Widget",
          graphic,
          id
        );
    }
    return res;
  };

  getGraphicsLayerJSON = (layer) => {
    return new Promise((myResolve, myReject) => {
      const graphics = APIHelper.getLayerGraphicsArray(layer);
      if (
        APIHelper.getLayerName(layer) === "PrintAreaLayer'" ||
        graphics.length === 0
      ) {
        myResolve(null);
      }
      APIHelper.projectGeometries(
        {
          geometries: graphics.map((graphic) => APIHelper.getGeometry(graphic)),
          outSR: APIHelper.constructSpatialReference(this.config.printSR),
        },
        this.props.map
      ).then((result) => {
        const newGraphics = result.map((i, index) => {
          return {
            attribute: graphics[index],
            symbol: APIHelper.getSymbol(graphics[index]),
            geometry: i,
          };
        });
        const res = {
          opacity: APIHelper.getLayerOpacity(layer),
          type: "Vector",
          styles: {},
          styleProperty: "_gx_style",
          geoJson: {
            type: "FeatureCollection",
            features: [],
          },
        };
        newGraphics.forEach((element, index) => {
          const geoJSON = this.getGraphicAsGeoJSON(element, index);
          res.styles[`${index}`] = geoJSON.symbol;
          res.geoJson.features.push(geoJSON.geometry);
        });
        myResolve(res);
      });
    });
  };

  getWMSLayerJSON = (layer) => {
    return new Promise((myResolve, myReject) => {
      const res = {
        opacity: APIHelper.getLayerOpacity(layer),
        type: "WMSLayer",
        version: APIHelper.getLayerVersion(layer),
        name: APIHelper.getLayerName(layer),
        baseURL: APIHelper.getLayerURL(layer),
        layers: APIHelper.getvisibleLayersOfWMS(layer).concat([]).reverse(),
      };
      myResolve(res);
    });
  };

  getServiceName = (url) => {
    const regex = /\/([^/.]*)\/(MapServer|FeatureServer)/gm;
    let m;
    while ((m = regex.exec(url)) !== null) {
      // This is necessary to avoid infinite loops with zero-width matches
      if (m.index === regex.lastIndex) {
        regex.lastIndex++;
      }
      return m[1];
    }
  };

  getBaseURL = (url) => {
    const regex = /(^http.*(MapServer|FeatureServer)\/)/gm;
    let m;
    while ((m = regex.exec(url)) !== null) {
      // This is necessary to avoid infinite loops with zero-width matches
      if (m.index === regex.lastIndex) {
        regex.lastIndex++;
      }
      return m[1];
    }
  };

  getMapLayerJSON = (layer) => {
    return new Promise((myResolve, myReject) => {
      const layerUrl = APIHelper.getLayerURL(layer);
      fetch(`${layerUrl}?f=pjson`, {
        method: "get",
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error("Something went wrong ...");
        })
        .then((data) => {
          const serviceName = this.getServiceName(layerUrl);
          let printDescription;
          if (layerUrl.toLowerCase().indexOf("/klima/") !== -1) {
            printDescription = `${serviceName}__${data.id}`.toLowerCase();
          } else {
            if (data.parentLayer) {
              printDescription = `${serviceName}_${data.parentLayer.name}_${data.name}`;
            } else {
              printDescription = `${serviceName}_${data.name}`;
            }
          }
          const res = {
            baseURL: this.getBaseURL(layerUrl),
            layer: data.name,
            layers: [`'show:${layer.sublayers.items[0].id}`], // TODO: was ist das => auslagern in APIHelper getLayerID gibt schon layer.id zurück
            name: APIHelper.getLayerID(layer),
            opacity: APIHelper.getLayerOpacity(layer),
            printDescription,
            type: "ArcGISDynamicMapServiceLayer",
          };
          myResolve(res);
        })
        .catch((error) => {
          myReject(error);
        });
    });
  };
  getFeatureLayerJSON = (layer) => {
    return new Promise((myResolve, myReject) => {
      const layerUrl = APIHelper.getLayerURL(layer).replace(
        /FeatureServer/g,
        "MapServer"
      );
      fetch(`${layerUrl}&f=pjson`, {
        method: "get",
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error("Something went wrong ...");
        })
        .then((data) => {
          const serviceName = this.getServiceName(layerUrl);
          let printDescription;
          if (data.parentLayer) {
            printDescription = `${serviceName}_${data.parentLayer.name}_${data.name}`;
          } else {
            printDescription = `${serviceName}_${data.name}`;
          }
          const res = {
            baseURL: this.getBaseURL(layerUrl),
            layer: data.name,
            layers: [`'show:${layer.layerId}`], // TODO: was ist das => auslagern in APIHelper getLayerID gibt schon layer.id zurück
            name: APIHelper.getLayerID(layer),
            opacity: APIHelper.getLayerOpacity(layer),
            printDescription,
            type: "ArcGISDynamicMapServiceLayer",
          };
          myResolve(res);
        })
        .catch((error) => {
          myReject(error);
        });
    });
  };

  getWebTileLayerJSON = (layer) => {
    return new Promise((myResolve) => {
      const opacity = APIHelper.getLayerOpacity(layer);
      const name = APIHelper.getLayerName(layer);
      const wmtsInfo = layer?.wmtsInfo;

      if (wmtsInfo?.url && wmtsInfo?.layerIdentifier) {
        myResolve({
          opacity,
          type: "WMTSLayer",
          name,
          baseURL: wmtsInfo.url,
          layer: wmtsInfo.layerIdentifier,
          matrixSet: wmtsInfo.tileMatrixSet || wmtsInfo.tileMatrixSetId,
          style: layer?.activeLayer?.styleId || "default",
          imageFormat: layer?.activeLayer?.imageFormat || "image/png",
          requestEncoding: "REST",
        });
        return;
      }

      if (layer?.url && layer?.activeLayer) {
        myResolve({
          opacity,
          type: "WMTSLayer",
          name,
          baseURL: layer.url,
          layer: layer.activeLayer.id,
          matrixSet: layer.activeLayer.tileMatrixSetId,
          style: layer.activeLayer.styleId || "default",
          imageFormat: layer.activeLayer.imageFormat || "image/png",
          requestEncoding: "REST",
        });
        return;
      }

      const templateUrl = layer?.urlTemplate || layer?.templateUrl;
      if (templateUrl) {
        myResolve({
          opacity,
          type: "XYZ",
          name,
          baseURL: templateUrl,
        });
        return;
      }

      myResolve(null);
    });
  };

  getTileLayerJSON = (layer) => {
    return new Promise((myResolve) => {
      const layerUrl = APIHelper.getLayerURL(layer);
      if (!layerUrl) {
        myResolve(null);
        return;
      }

      myResolve({
        opacity: APIHelper.getLayerOpacity(layer),
        type: "ArcGISTiledMapServiceLayer",
        name: APIHelper.getLayerName(layer),
        baseURL: layerUrl,
      });
    });
  };

  getLayerJSON = () => {
    return new Promise((myResolve, myReject) => {
      const me = this;
      const res = [];
      let layers = [];
      APIHelper.getAllLayersOfMap(this.props.map).forEach((layer) => {
        if (
          APIHelper.isLayerVisible(layer, this.props.map, this.props.mapView)
        ) {
          layers.push(layer);
        }
      });
      layers = layers.filter((layer) => {
        if (
          me.config.nonPrintableLayers.indexOf(APIHelper.getLayerName(layer)) !== -1
        ) { return false; }
        return true;
      });


      layers
        .reduce((previousPromise, layer) => {
          return previousPromise.then((json) => {
            if (json) {
              res.push(json);
            }

            if (APIHelper.isWMSLayer(layer)) {
              return this.getWMSLayerJSON(layer);
            }
            if (APIHelper.isFeatureLayer(layer)) {
              return this.getFeatureLayerJSON(layer);
            }
            if (APIHelper.isGraphicsLayer(layer)) {
              return this.getGraphicsLayerJSON(layer);
            }
            if (APIHelper.isMapLayer(layer)) {
              return this.getMapLayerJSON(layer);
            }
            if (layer?.type === "wmts" || layer?.type === "web-tile") {
              return this.getWebTileLayerJSON(layer);
            }
            if (layer?.type === "tile") {
              return this.getTileLayerJSON(layer);
            }
          });
        }, Promise.resolve())
        .then((json) => {
          if (json) {
            res.push(json);
          }
          myResolve(res);
        });
    });
  };

  getPrintJSON = (printArea) => {
    return new Promise((myResolve, myReject) => {
      const extent = printArea;
      const bbox = [
        Math.min(extent.xmin, extent.xmax),
        Math.max(extent.ymin, extent.ymax),
        Math.max(extent.xmin, extent.xmax),
        Math.min(extent.ymin, extent.ymax),
      ];
      const center = [
        extent.xmin + Math.abs(extent.xmax - extent.xmin) / 2,
        extent.ymin + Math.abs(extent.ymax - extent.ymin) / 2,
      ];

      const json = {
        layout: this.props.mapTemplate.display, // TODO ?
        layout_template: this.props.mapTemplate.value, // TODO ?
        srs: `EPSG:${this.config.printSR}`,
        units: "m",
        app: "config",
        rotation: 0,
        lang: "de",
        dpi: `${this.config.dpi}`,
        title: this.props.mapTitle,
        movie: false,
        pages: [
          {
            center,
            bbox,
            rotation: 0,
            langde: true,
          },
        ],
      };
      if (this.props.mapScale) {
        if (this.props.mapScale.value === "0.0") {
          json.pages[0].scale = `${APIHelper.getMapScale(
            this.props.map,
            this.props.mapView
          )}`;
        } else {
          json.pages[0].scale = this.props.mapScale.value;
        }
      }
      if (this.props.useLegend) {
        json.enableLegends = true;
      } else {
        delete json.legends;
      }
      this.getLayerJSON().then(
        (layersJson) => {
          json.layers = layersJson;
          json.legends = layersJson.filter((layer) => layer.type != "WMTSLayer").map((element) => element.name);
          myResolve(json);
        },
        (error) => {
          console.error(error);
        }
      );
    });
  };

  print = () => {
    const me = this;
    const printArea =
      this.props.printArea ||
      APIHelper.getMapExtent(this.props.map, this.props.mapView);
    APIHelper.projectGeometries(
      {
        geometries: [printArea],
        outSR: APIHelper.constructSpatialReference(this.config.printSR),
      },
      this.props.map,
      this.props.mapView
    ).then(
      function (projectedGeometries) {
        me.getPrintJSON(projectedGeometries[0])
          .then((printJSON) => {
            fetch(me.config.printURL, {
              method: "post",
              body: JSON.stringify(printJSON),
              headers: new Headers({ "content-type": "application/json" }),
            })
              .then((response) => {
                if (response.ok) {
                  return response.json();
                }
                throw new Error("Something went wrong ...");
              })
              .then((data) => {
                me.onPrintReady(data.getURL);
              })
              .catch((error) => {
                me.onPrintError(error);
              });
          })
          .catch(function (error) {
            console.log(error);
          });
      },
      (error) => console.log(error)
    );
  };

  render = () => {
    return null;
  };
}
export default PrintLogik;
